import { useRef, useState, useEffect, useCallback } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { ProcessSteps, TechMarquee, FAQ } from "../components/Sections";
import { Reveal } from "../components/motion";
import { openConsultationModal } from "../components/ConsultationModal";
import { SERVICES, PROJECTS, FAQS, getServiceFaqs, LEGAL_ENTITY } from "../data";
import {
  ArrowRight,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  Zap,
  Layers,
  Cpu,
  Database,
  Workflow,
  Clock,
  CheckCircle2,
  Lock,
  Activity,
  Server,
  Boxes,
  Code2,
  Globe,
  Radio,
  Brain,
  Compass,
  CircuitBoard,
  BriefcaseBusiness,
  Users2,
  Smartphone,
  RefreshCw,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* MAIN REDESIGNED SERVICE DETAIL PAGE                                */
/* ------------------------------------------------------------------ */
export default function ServiceDetail({ forcedSlug }) {
  const { slug: routeSlug } = useParams();
  const slug = forcedSlug || routeSlug || "software-development";
  const WHITE_BG_SERVICE_SLUGS = [
    "software-development",
    "ai-solutions",
    "iot-solutions",
    "cloud-infrastructure",
    "consultancy",
  ];
  const isWhiteBgService = WHITE_BG_SERVICE_SLUGS.includes(slug);
  const [activeProjectIdx, setActiveProjectIdx] = useState(0);
  const timerRef = useRef(null);

  const service = SERVICES.find((s) => s.slug === slug);
  const Icon = service?.icon || Brain;

  const relevantProjects = PROJECTS.filter((p) => {
    if (slug === "mobile-apps") return p.cat === "Mobile";
    if (slug === "web-development") return p.cat === "Web";
    if (slug === "software-development" || slug === "custom-software") {
      return ["buildhealer", "indusdeal", "pawsup", "clueblu", "launchpad"].includes(p.slug);
    }
    if (slug === "ai-solutions" || slug === "ai-development" || slug === "ai-automation") {
      return ["indusdeal", "foxrun", "pawsup", "clueblu", "launchpad"].includes(p.slug);
    }
    if (slug === "cloud-infrastructure" || slug === "cloud-devops" || slug === "cybersecurity") {
      return ["indusdeal", "buildhealer", "pawsup", "clueblu", "launchpad"].includes(p.slug);
    }
    if (slug === "iot-solutions" || slug === "iot") {
      return ["pawsup", "foxrun", "lockiva", "aqua5plus", "ciaztravels"].includes(p.slug);
    }
    if (slug === "erp" || slug === "hrms" || slug === "crm") {
      return ["indusdeal", "buildhealer", "clueblu", "launchpad", "paineasy"].includes(p.slug);
    }
    return ["buildhealer", "ecopro-builders", "indusdeal", "clueblu", "launchpad"].includes(p.slug);
  });

  const displayProjects =
    relevantProjects.length >= 5
      ? relevantProjects
      : [...relevantProjects, ...PROJECTS.filter((p) => !relevantProjects.some((rp) => rp.slug === p.slug))].slice(0, 5);

  // Auto-shuffle motion timer: continuously auto-shuffles every 3.2s
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (displayProjects.length > 1) {
      timerRef.current = setInterval(() => {
        setActiveProjectIdx((prev) => (prev + 1) % displayProjects.length);
      }, 3200);
    }
  }, [displayProjects.length]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  if (!service) return <Navigate to="/services" replace />;
  if (service.canonicalSlug) return <Navigate to={`/services/${service.canonicalSlug}`} replace />;

  const handlePrevProject = () => {
    setActiveProjectIdx((prev) => (prev - 1 + displayProjects.length) % displayProjects.length);
    resetTimer();
  };

  const handleNextProject = () => {
    setActiveProjectIdx((prev) => (prev + 1) % displayProjects.length);
    resetTimer();
  };

  const handleSelectProject = (idx) => {
    setActiveProjectIdx(idx);
    resetTimer();
  };

  const getCardStyle = (diff) => {
    switch (diff) {
      case 0:
        return {
          x: "0%",
          scale: 1,
          y: -10,
          zIndex: 30,
          opacity: 1,
          rotateY: 0,
          filter: "blur(0px) brightness(1)",
          pointerEvents: "auto",
        };
      case -1:
        return {
          x: "-68%",
          scale: 0.82,
          y: 0,
          zIndex: 20,
          opacity: 0.72,
          rotateY: 8,
          filter: "blur(0.5px) brightness(0.85)",
          pointerEvents: "auto",
        };
      case 1:
        return {
          x: "68%",
          scale: 0.82,
          y: 0,
          zIndex: 20,
          opacity: 0.72,
          rotateY: -8,
          filter: "blur(0.5px) brightness(0.85)",
          pointerEvents: "auto",
        };
      case -2:
        return {
          x: "-128%",
          scale: 0.66,
          y: 8,
          zIndex: 10,
          opacity: 0.35,
          rotateY: 16,
          filter: "blur(1px) brightness(0.7)",
          pointerEvents: "auto",
        };
      case 2:
        return {
          x: "128%",
          scale: 0.66,
          y: 8,
          zIndex: 10,
          opacity: 0.35,
          rotateY: -16,
          filter: "blur(1px) brightness(0.7)",
          pointerEvents: "auto",
        };
      default:
        return {
          x: diff < 0 ? "-160%" : "160%",
          scale: 0.5,
          y: 20,
          zIndex: 0,
          opacity: 0,
          rotateY: diff < 0 ? 25 : -25,
          filter: "blur(2px) brightness(0.5)",
          pointerEvents: "none",
        };
    }
  };

  const getFeatureDescription = (feat, svcSlug) => {
    const descriptions = {
      // Technology Consultancy
      "Fractional CTO leadership & engineering team scaling":
        "Executive technical leadership, architectural governance, and engineering culture to accelerate sprint velocity without the cost of a full-time executive.",
      "Systems architecture audits & technical due diligence":
        "In-depth analysis of codebases, databases, API layers, and security postures for technical due diligence, acquisitions, and high-concurrency scaling.",
      "Cloud economics & infrastructure cost optimization":
        "Workload right-sizing across AWS, GCP, and Azure, eliminating zombie resources and planning reserved commitments to cut cloud spend by 30-50%.",
      "Legacy modernization & cloud migration roadmaps":
        "De-risking legacy transitions using Strangler-Fig patterns, microservices decoupling, and zero-downtime data migrations.",
      "AI transformation strategy & tech stack selection":
        "Pragmatic evaluation of foundation models, enterprise vector databases, and agent pipelines to target high-ROI opportunities and avoid vendor lock-in.",
      "Security, compliance & risk assessment":
        "Comprehensive vulnerability assessments, SOC 2, DPDP, and GDPR compliance readiness, and zero-trust policy implementation.",

      // AI Solutions
      "Custom LLM fine-tuning & domain-adapted intelligence":
        "Domain-specialized model weights, private dataset fine-tuning, and task-specific optimization delivering enterprise-grade reasoning.",
      "Enterprise RAG with vectorized semantic knowledge search":
        "Sub-second semantic search and context retrieval over private enterprise documents, ERPs, and knowledge repositories with zero data leakage.",
      "Autonomous agent orchestration for business workflows":
        "Multi-agent networks that reason, delegate tasks, integrate with enterprise APIs, and execute end-to-end multi-step operational workflows.",
      "Real-time computer vision & predictive MLOps pipelines":
        "High-throughput edge and cloud vision inference, automated model retraining loops, and real-time anomaly detection pipelines.",
      "AI guardrails, safety & prompt engineering":
        "Deterministic safety filters, hallucination mitigation, token efficiency controls, and structured output formatting for enterprise compliance.",
      "Continuous model evaluation & retraining workflows":
        "Automated drift detection, synthetic benchmark testing, and active learning feedback loops that maintain production accuracy.",

      // Software Development
      "Modern Next.js & React web platforms and portals":
        "High-performance, SEO-first web applications and dashboards with responsive architecture, micro-animations, and instant page loads.",
      "Native-feel iOS & Android mobile apps (Flutter / React Native)":
        "Feature-complete cross-platform mobile apps with native fluid performance, offline data persistence, and push notifications.",
      "Bespoke business operating systems with zero licensing bloat":
        "Tailor-made internal business platforms that unify workflows, operations, and reporting without recurring per-seat software licenses.",
      "Scalable REST & GraphQL microservice backends":
        "Resilient API microservices engineered in Go, Node.js, and Python with horizontal autoscaling and sub-20ms response times.",
      "Cloud-native database design & event architectures":
        "High-throughput PostgreSQL, MongoDB, Redis, and event streams engineered for strict data integrity and real-time synchronization.",
      "Continuous CI/CD deployment & technical maintenance":
        "Automated multi-environment deployment pipelines, automated test suites, zero-downtime rollouts, and 24/7 reliability maintenance.",

      // IoT & Automation
      "Sensor hardware connectivity & edge microcontroller processing":
        "Embedded firmware for microcontrollers interfacing with environmental, industrial, and electrical sensors.",
      "Real-time telemetry streaming, alerting & anomalies detection":
        "High-speed time-series ingestion pipelines with automated threshold triggers and predictive anomaly notifications.",
      "Remote device fleet management & secure OTA updates":
        "Centralized fleet dashboard for device provisioning, remote health diagnostics, and encrypted over-the-air firmware updates.",
      "Industrial workflow automation & predictive diagnostics":
        "Bridging sensor telemetry with SCADA and cloud automation to trigger instant operational interventions before hardware failure.",
      "Edge computing data aggregation & filtering":
        "Local on-device data compression and edge inferencing to minimize cloud bandwidth while preserving critical telemetry events.",
      "Encrypted device-to-cloud MQTT & TLS telemetry":
        "End-to-end encrypted MQTT and TLS communication securing device-to-cloud data transmission across cellular and LoRaWAN networks.",

      // Cloud Infrastructure
      "Dedicated bare metal & container clusters on neuravolt.cloud":
        "Ultra-fast dedicated bare-metal servers and container clusters operating on neuravolt.cloud with zero virtualization overhead.",
      "Ultra-low latency global routing & edge delivery":
        "Global Anycast routing and optimized edge network delivering single-digit millisecond response times worldwide.",
      "Zero-downtime high-availability architecture":
        "Multi-zone redundancy, automated failover routing, and container self-healing ensuring uninterrupted service availability.",
      "Automated multi-cloud IaC (AWS, GCP, Azure)":
        "Production-tested Terraform and Pulumi infrastructure as code for reproducible, auditable, and version-controlled environments.",
      "24/7 proactive monitoring & disaster recovery":
        "Continuous Prometheus and Grafana telemetry, automated multi-region backups, and point-in-time disaster recovery SLAs.",
      "Dedicated IP allocation & custom DNS routing":
        "Clean enterprise IP allocations, DDoS mitigation filtering, and ultra-fast DNS routing tailored for enterprise domains.",
    };

    return descriptions[feat] || "Engineered to enterprise standards, seamlessly integrated with your business workflows, APIs, and operational requirements.";
  };

  const getSystemTopology = () => {
    if (slug === "consultancy") {
      return [
        {
          step: "01",
          title: "Discovery & Audit",
          desc: "Deep-dive codebase inspection, infrastructure cost profiling, and technical debt mapping across all systems.",
          icon: BriefcaseBusiness,
        },
        {
          step: "02",
          title: "Architecture Strategy",
          desc: "Designing scalable target topologies, cloud migration roadmaps, and optimal technology stack selection.",
          icon: Workflow,
        },
        {
          step: "03",
          title: "Governance & De-Risking",
          desc: "Proof-of-concept validation, SOC 2 / compliance gap mitigation, and automated cloud cost controls.",
          icon: ShieldCheck,
        },
        {
          step: "04",
          title: "Executive Execution",
          desc: "Hands-on engineering oversight, development velocity acceleration, and milestone-driven team handover.",
          icon: Zap,
        },
      ];
    }
    if (slug === "software-development") {
      return [
        {
          step: "01",
          title: "Architecture & Design",
          desc: "Interactive Figma design systems, database ERDs, and REST/GraphQL API specifications.",
          icon: Layers,
        },
        {
          step: "02",
          title: "Agile Engineering",
          desc: "Bi-weekly sprint releases with production-quality code, unit tests, and continuous CI/CD staging builds.",
          icon: Code2,
        },
        {
          step: "03",
          title: "QA & Security Hardening",
          desc: "Automated end-to-end testing, static code analysis, load testing, and penetration security audits.",
          icon: ShieldCheck,
        },
        {
          step: "04",
          title: "Production Cutover",
          desc: "Zero-downtime cloud deployment, automated monitoring, operational runbooks, and 100% IP transfer.",
          icon: Zap,
        },
      ];
    }
    if (slug === "iot-solutions" || slug === "iot") {
      return [
        {
          step: "01",
          title: "Sensor & Edge Layer",
          desc: "Hardened microcontrollers, industrial sensor calibration, and localized telemetry filtering.",
          icon: CircuitBoard,
        },
        {
          step: "02",
          title: "Ingestion & Security",
          desc: "Mutual TLS encrypted MQTT broker communication with sub-second time-series stream ingestion.",
          icon: ShieldCheck,
        },
        {
          step: "03",
          title: "Analytics & Telemetry",
          desc: "Real-time time-series database indexing, threshold monitoring, and predictive anomaly triggers.",
          icon: Database,
        },
        {
          step: "04",
          title: "Automated Control",
          desc: "Automated actuators, SCADA integrations, live executive dashboards, and instant remote alerts.",
          icon: Zap,
        },
      ];
    }
    if (slug === "cloud-infrastructure" || slug === "cloud-devops") {
      return [
        {
          step: "01",
          title: "Resource Provisioning",
          desc: "Dedicated bare-metal servers and isolated Kubernetes clusters deployed with Terraform IaC.",
          icon: Server,
        },
        {
          step: "02",
          title: "Network & Anycast",
          desc: "Ultra-low latency Anycast BGP routing, DDoS scrubbing, and automated SSL/TLS termination.",
          icon: Globe,
        },
        {
          step: "03",
          title: "Security & Observability",
          desc: "Zero-trust network micro-segmentation, continuous Prometheus metrics, and automated audit logging.",
          icon: ShieldCheck,
        },
        {
          step: "04",
          title: "Automated Recovery",
          desc: "Self-healing pod rescheduling, cross-region failovers, and automated snapshot disaster recovery.",
          icon: Zap,
        },
      ];
    }
    return [
      {
        step: "01",
        title: "Data & Ingestion",
        desc: "Real-time telemetry, structured enterprise databases, REST/GraphQL APIs, and document streams.",
        icon: Database,
      },
      {
        step: "02",
        title: "Intelligence & Logic",
        desc: "Domain-adapted LLMs, vectorized knowledge indexing, and autonomous multi-agent orchestration.",
        icon: Brain,
      },
      {
        step: "03",
        title: "Security & Guardrails",
        desc: "Air-gapped models, zero data leakage, policy audits, and sub-second anomaly mitigation.",
        icon: ShieldCheck,
      },
      {
        step: "04",
        title: "Operational Action",
        desc: "Automated write-backs to ERP, CRM, mobile apps, web dashboards, and customer channels.",
        icon: Zap,
      },
    ];
  };


  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.seoDesc || service.desc,
    "url": `https://neuravolt.in/services/${slug}`,
    "provider": {
      "@type": "Organization",
      "@id": "https://neuravolt.in/#organization",
      "name": "NeuraVolt Technology",
      "legalName": LEGAL_ENTITY,
      "url": "https://neuravolt.in"
    },
    "areaServed": [
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "United Arab Emirates" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": service.title,
      "itemListElement": (service.features || []).map((f) => ({
        "@type": "Offer",
        "itemOffered": { "@type": "Service", "name": f }
      }))
    }
  };

  const serviceFaqs = getServiceFaqs(slug);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://neuravolt.in/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://neuravolt.in/services" },
      { "@type": "ListItem", "position": 3, "name": service.title, "item": `https://neuravolt.in/services/${slug}` }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": serviceFaqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };

  const getHeroBgImage = () => {
    if (slug === "software-development" || slug === "custom-software") {
      return "/brand/enterprise-ai-supercomputer-hd.jpg";
    }
    if (slug === "cloud-infrastructure" || slug === "cloud-devops") {
      return "/brand/cloud-infrastructure-hero-hd.jpg";
    }
    if (slug === "iot-solutions" || slug === "iot") {
      return "/brand/iot-solutions-hero-hd.jpg";
    }
    if (slug === "consultancy") {
      return "/brand/consultancy-hero-hd.jpg";
    }
    if (slug === "ai-solutions" || slug === "ai" || slug === "ai-development" || slug === "ai-automation") {
      return "/brand/ai-solutions-hero-real.jpg";
    }
    return "/brand/ai-solutions-hero-real.jpg";
  };

  const jsonLd = [serviceSchema, breadcrumbSchema, faqSchema];

  return (
    <Layout>
      <Seo
        title={service.seoTitle || `${service.title} Services | NeuraVolt`}
        description={service.seoDesc || `${service.desc} Professional ${service.title.toLowerCase()} services engineered by Neuravolt, India's AI-first software engineering company.`}
        keywords={service.seoKeywords || `${service.title}, ${service.title} services India, ${service.title} architecture, Neuravolt ${service.title.toLowerCase()}`}
        path={`/services/${slug}`}
        jsonLd={jsonLd}
      />

      {/* ========================================================= */}
      {/* 1. HERO SECTION (Executive Enterprise Redesign & HD BG) */}
      {/* ========================================================= */}
      <section className="relative overflow-hidden pt-24 sm:pt-28 md:pt-32 pb-11 sm:pb-14 px-5 sm:px-8 bg-[#0B0B0B] text-white border-b border-white/[0.08]">
        {/* Background Image Layer (Contextual 3D Architecture per Service) */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <img
            src={getHeroBgImage()}
            alt=""
            loading="eager"
            fetchpriority="high"
            className="w-full h-full object-cover object-right md:object-center opacity-70 sm:opacity-85 filter contrast-105 brightness-100"
          />
          {/* Ambient Tech Gradients & Readability Overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/75 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-transparent to-[#0B0B0B]/35" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_80%_45%,rgba(255,90,31,0.14),transparent_70%)]" />
        </div>

        {/* Animated Floating Glow Orbs */}
        <div className="absolute top-16 left-1/4 w-80 h-80 rounded-full bg-[#FF5A1F]/15 blur-[100px] pointer-events-none animate-pulse" />
        <div className="absolute top-32 right-1/4 w-72 h-72 rounded-full bg-amber-400/10 blur-[90px] pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Breadcrumb Navigation (Shifted cleanly below top logo) */}
          <nav className="flex items-center gap-2 text-xs font-mono text-neutral-400 mb-5 sm:mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF5A1F] transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3 text-neutral-600" />
            <Link to="/services" className="hover:text-[#FF5A1F] transition-colors">Services</Link>
            <ChevronRight className="w-3 h-3 text-neutral-600" />
            <span className="text-[#FF5A1F] font-semibold">{service.title}</span>
          </nav>

          {/* Redesigned Commanding Content Layout (No right-side element) */}
          <div className="max-w-4xl text-left">
            {/* Main Hero Title */}
            <Reveal delay={0.1}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[3.9rem] font-bold text-white tracking-tight leading-[1.08] mb-4 sm:mb-5">
                {service.title} Built For <span className="text-gradient">Measurable Scale</span>
              </h1>
            </Reveal>

            {/* Hero Description */}
            <Reveal delay={0.15}>
              <p className="text-base sm:text-lg lg:text-xl text-neutral-300/95 max-w-3xl leading-relaxed mb-6 sm:mb-7 font-normal">
                {service.desc}
              </p>
            </Reveal>

            {/* Dual Action CTAs */}
            <Reveal delay={0.2}>
              <div className="flex flex-wrap items-center gap-4 mb-7 sm:mb-8">
                <button
                  onClick={() => openConsultationModal(service.title)}
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 sm:py-4 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-all shadow-[0_8px_20px_-4px_rgba(255,90,31,0.35)] hover:shadow-[0_12px_28px_-4px_rgba(255,90,31,0.5)] group cursor-pointer active:scale-[0.98]"
                >
                  <span>Book A Free Call</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>

                <a
                  href="#capabilities"
                  className="inline-flex items-center gap-2 px-7 py-3.5 sm:py-4 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.14] hover:border-[#FF5A1F]/50 font-semibold text-sm text-neutral-200 hover:text-white transition-all backdrop-blur-sm active:scale-[0.98]"
                >
                  <Boxes className="w-4 h-4 text-[#FF5A1F]" />
                  <span>See Capabilities & Approach</span>
                </a>

                {service.externalUrl && (
                  <a
                    href={service.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3.5 sm:py-4 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-neutral-300 hover:text-white font-medium text-sm transition-colors border border-white/[0.08]"
                  >
                    <span>Visit {service.externalUrl.replace("https://", "")}</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ========================================================= */}
      {/* 2. CORE CAPABILITIES (Solution Grid inspired by Intellinez) */}
      {/* ========================================================= */}
      <section className="py-10 sm:py-14 px-5 sm:px-8 bg-white border-b border-black/[0.06]" id="capabilities">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-[#0B0B0B] tracking-tight mb-3">
              What We <span className="text-gradient">Engineer & Deliver</span>
            </h2>
            <p className="text-neutral-600 text-base sm:text-lg">
              {slug === "consultancy"
                ? "Strategic engineering leadership, architecture de-risking, and cloud economics — not theoretical slide decks."
                : slug === "software-development"
                ? "High-performance web platforms, cross-platform mobile apps, and scalable microservices engineered for production excellence."
                : "Engineered for complexity, high concurrency, and measurable enterprise leverage — not generic templates bent into shape."}
            </p>
          </div>

          {/* 2x3 Interactive Solution Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {(service.features || []).map((feature, idx) => {
              // Contextual capability icons
              const capabilityIcons =
                slug === "software-development" || slug === "custom-software"
                  ? [Globe, Smartphone, Layers, Server, Database, RefreshCw]
                  : slug === "consultancy"
                  ? [Users2, BriefcaseBusiness, Layers, Workflow, Brain, ShieldCheck]
                  : [Sparkles, Cpu, Layers, Workflow, ShieldCheck, Database];
              const CapIcon = capabilityIcons[idx % capabilityIcons.length];

              return (
                <Reveal key={feature} delay={idx * 0.08}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="group glass-card rounded-3xl p-6 sm:p-7 border border-black/[0.06] shadow-sm hover:shadow-xl hover:border-[#FF5A1F]/40 transition-all duration-300 h-full bg-[#FAF9F6]/50"
                  >
                    {/* Illuminated Icon Badge */}
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FF5A1F]/15 to-[#FF5A1F]/5 border border-[#FF5A1F]/20 flex items-center justify-center text-[#FF5A1F] mb-4 shadow-2xs group-hover:scale-110 group-hover:bg-[#FF5A1F] group-hover:text-white transition-all duration-300">
                      <CapIcon className="w-5 h-5" />
                    </div>

                    {/* Feature Title */}
                    <h3 className="font-heading font-bold text-lg sm:text-xl text-[#0B0B0B] group-hover:text-[#FF5A1F] transition-colors mb-2 leading-snug">
                      {feature}
                    </h3>

                    {/* Descriptive context */}
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {getFeatureDescription(feature, slug)}
                    </p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================================================= */}
      {/* 3. ARCHITECTURE IN ACTION (Interactive Pipeline Schematic) */}
      {/* ========================================================= */}
      <section className="py-10 sm:py-14 px-5 sm:px-8 mesh-light border-b border-black/[0.06] overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-[#0B0B0B] tracking-tight mb-3">
              {slug === "software-development" ? (
                <>How Your Software Is <span className="text-gradient">Engineered for Scale</span></>
              ) : slug === "consultancy" ? (
                <>How We Guide <span className="text-gradient">Your Technology Transformation</span></>
              ) : (
                <>How Your Solution <span className="text-gradient">Operates at Scale</span></>
              )}
            </h2>
            <p className="text-neutral-600 text-base sm:text-lg">
              {slug === "software-development"
                ? "A structured, full-stack engineering lifecycle connecting interactive Figma design, agile sprint delivery, automated QA security hardening, and zero-downtime cutover."
                : slug === "consultancy"
                ? "A structured, four-phase engineering advisory methodology connecting discovery, architecture, governance, and velocity."
                : "A high-security, low-latency architecture connecting ingest, reasoning, safety guardrails, and enterprise APIs."}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 relative">
            {getSystemTopology().map((st, i) => {
              const StIcon = st.icon;
              return (
                <Reveal key={st.step} delay={i * 0.1}>
                  <div className="glass-card rounded-3xl p-6 sm:p-7 border border-black/[0.06] bg-white shadow-sm hover:shadow-md hover:border-[#FF5A1F]/30 transition-all duration-300 h-full relative group">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-xs font-bold text-[#FF5A1F] bg-[#FF5A1F]/10 px-2.5 py-1 rounded-md">
                        {st.step}
                      </span>
                      <div className="w-9 h-9 rounded-xl bg-neutral-100 group-hover:bg-[#FF5A1F] group-hover:text-white flex items-center justify-center text-neutral-600 transition-colors">
                        <StIcon className="w-4 h-4" />
                      </div>
                    </div>

                    <h4 className="font-heading font-bold text-lg text-neutral-900 mb-2">
                      {st.title}
                    </h4>
                    <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                      {st.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================================================= */}
      {/* 4. PROVEN CASE STUDIES (Intellinez Mini-Case Reference)    */}
      {/* ========================================================= */}
      {displayProjects.length > 0 && (
        <section className="py-8 sm:py-10 px-4 sm:px-6 lg:px-8 bg-[#0B0B0B] text-white relative overflow-hidden" id="case-studies">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-4 sm:mb-6">
              <div>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-display font-bold">
                  Case Studies in {service.title}
                </h2>
              </div>
              <Link
                to="/portfolio"
                className="text-sm font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1.5 shrink-0"
              >
                <span>View all portfolio projects</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* 3D Interactive 5-Card Carousel with Continuous Auto-Shuffle & Full Manual Control */}
            <div className="relative py-2 sm:py-4 flex items-center justify-center select-none">
              {/* Left Arrow Button */}
              <button
                type="button"
                onClick={handlePrevProject}
                aria-label="Previous case study"
                className="absolute left-2 sm:left-4 md:left-6 lg:left-8 top-1/2 -translate-y-1/2 z-40 w-11 h-11 sm:w-13 sm:h-13 md:w-14 md:h-14 rounded-full bg-[#141414]/90 hover:bg-[#FF5A1F] border border-white/15 hover:border-[#FF5A1F] text-white/90 hover:text-white shadow-[0_8px_30px_rgba(0,0,0,0.7)] hover:shadow-[0_0_28px_rgba(255,90,31,0.6)] backdrop-blur-xl grid place-items-center group transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer ring-1 ring-white/10 hover:ring-[#FF5A1F]/50"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 group-hover:-translate-x-0.5 transition-transform duration-300" />
              </button>

              {/* Right Arrow Button */}
              <button
                type="button"
                onClick={handleNextProject}
                aria-label="Next case study"
                className="absolute right-2 sm:right-4 md:right-6 lg:right-8 top-1/2 -translate-y-1/2 z-40 w-11 h-11 sm:w-13 sm:h-13 md:w-14 md:h-14 rounded-full bg-[#141414]/90 hover:bg-[#FF5A1F] border border-white/15 hover:border-[#FF5A1F] text-white/90 hover:text-white shadow-[0_8px_30px_rgba(0,0,0,0.7)] hover:shadow-[0_0_28px_rgba(255,90,31,0.6)] backdrop-blur-xl grid place-items-center group transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer ring-1 ring-white/10 hover:ring-[#FF5A1F]/50"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-0.5 transition-transform duration-300" />
              </button>

              {/* Perspective 3D Stage */}
              <div
                className="relative w-full max-w-[280px] sm:max-w-[310px] md:max-w-[330px] h-[430px] sm:h-[450px] mx-auto flex items-center justify-center"
                style={{ perspective: 1200 }}
              >
                {displayProjects.map((p, idx) => {
                  const len = displayProjects.length;
                  let diff = (idx - activeProjectIdx) % len;
                  if (diff > len / 2) diff -= len;
                  if (diff < -len / 2) diff += len;

                  const isCenter = diff === 0;
                  const style = getCardStyle(diff);
                  const logoUrl = p.logo
                    ? p.logo
                    : p.domain
                    ? `https://www.google.com/s2/favicons?domain=${p.domain}&sz=128`
                    : null;

                  return (
                    <motion.div
                      key={p.slug}
                      animate={{
                        x: style.x,
                        scale: style.scale,
                        y: style.y,
                        opacity: style.opacity,
                        rotateY: style.rotateY,
                        zIndex: style.zIndex,
                        filter: style.filter,
                      }}
                      transition={{ type: "spring", stiffness: 220, damping: 24, mass: 0.8 }}
                      whileHover={isCenter ? { y: -14, scale: 1.02 } : {}}
                      drag={isCenter ? "x" : false}
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.25}
                      onDragEnd={(e, { offset, velocity }) => {
                        if (offset.x > 45 || velocity.x > 250) {
                          handlePrevProject();
                        } else if (offset.x < -45 || velocity.x < -250) {
                          handleNextProject();
                        }
                      }}
                      onClick={(e) => {
                        if (!isCenter) {
                          e.preventDefault();
                          handleSelectProject(idx);
                        }
                      }}
                      className={`absolute inset-0 w-full h-full rounded-2xl p-4 sm:p-5 flex flex-col justify-between transition-colors duration-300 select-none ${
                        isCenter
                          ? "bg-[#141414] border border-white/20 shadow-2xl cursor-pointer"
                          : "bg-neutral-900/90 border border-white/10 hover:border-white/20 shadow-xl backdrop-blur-md cursor-pointer"
                      }`}
                      style={{
                        transformStyle: "preserve-3d",
                        pointerEvents: style.pointerEvents,
                      }}
                    >
                      <Link
                        to={`/portfolio/${p.slug}`}
                        onClick={(e) => {
                          if (!isCenter) {
                            e.preventDefault();
                            handleSelectProject(idx);
                          }
                        }}
                        className="flex flex-col justify-between h-full group"
                      >
                        <div>
                          <div className="relative aspect-[16/10] rounded-xl overflow-hidden mb-3.5 border border-white/10 bg-black/80 flex items-center justify-center p-1.5">
                            <img
                              src={p.img}
                              alt={p.title}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                            />
                            <span className="absolute top-2.5 right-2.5 text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-[#FF5A1F] text-white shadow-sm">
                              {slug === "software-development"
                                ? (p.cat === "Mobile" ? "Mobile App" : "Web Platform")
                                : p.cat}
                            </span>

                          </div>

                          <div className="flex items-center gap-2 mb-2">
                            {logoUrl && (
                              <img
                                src={logoUrl}
                                alt=""
                                className="w-4 h-4 rounded-md object-contain border border-white/20"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                            <h3
                              className={`font-heading text-base sm:text-lg font-bold transition-colors ${
                                isCenter ? "text-white" : "text-white/80 group-hover:text-white"
                              }`}
                            >
                              {p.title}
                            </h3>
                          </div>

                          <p className="text-neutral-400 text-xs line-clamp-2 leading-relaxed mb-3">
                            {p.desc}
                          </p>
                        </div>

                        <div className="pt-3 border-t border-white/10 flex items-center justify-center text-[11px]">
                          <span className="text-[#FF5A1F] font-semibold inline-flex items-center gap-1.5 group-hover:translate-x-1 transition-transform">
                            Read Case Study <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Pagination Indicators / Dots */}
            <div className="flex items-center justify-center gap-2 mt-2 sm:mt-3">
              {displayProjects.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectProject(idx)}
                  className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    idx === activeProjectIdx
                      ? "w-8 bg-[#FF5A1F] shadow-[0_0_12px_rgba(255,90,31,0.7)]"
                      : "w-2 bg-white/20 hover:bg-white/40"
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========================================================= */}
      {/* 5. PROCESS, TECH STACK & FAQ SECTIONS                     */}
      {/* ========================================================= */}
      <ProcessSteps whiteBg={isWhiteBgService} eyebrow={null} />
      <TechMarquee eyebrow={null} />
      <FAQ
        whiteBg={isWhiteBgService}
        items={serviceFaqs}
        eyebrow={null}
        title={<>Frequently Asked <span className="text-gradient">Questions</span></>}
      />

      {/* ========================================================= */}
      {/* 6. INTELLINEZ-STYLE FINAL CALLOUT & DIRECT CONSULTATION   */}
      {/* ========================================================= */}
      <section className="py-8 sm:py-12 px-5 sm:px-8 mesh-light">
        <div className="max-w-6xl mx-auto rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-10 lg:p-12 bg-[#0B0B0B] text-white text-center relative overflow-hidden border border-white/15 shadow-2xl">
          {/* Background Image: sharp team collaboration meeting photo without blur */}
          <div className="absolute inset-0 z-0">
            <img
              src="/brand/cta-meeting-bg.jpg"
              alt="NeuraVolt Strategy & Engineering Team"
              className="w-full h-full object-cover object-center"
            />
            {/* Contrast overlay: keeps team meeting clearly visible while ensuring text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/85" />
            <div className="absolute inset-0 bg-black/20" />
          </div>

          <div className="blob w-[360px] h-[360px] bg-[#FF5A1F] top-0 left-1/3 animate-aurora opacity-25 pointer-events-none" />

          <div className="relative z-10 max-w-3xl mx-auto space-y-4 sm:space-y-5">
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Ready To Build Your <span className="text-gradient">{service.title} Solution?</span>
            </h2>
            <p className="text-neutral-300 text-sm sm:text-base leading-relaxed">
              {slug === "software-development"
                ? "Book a free discovery call with our engineering leadership. We will evaluate your technical specifications, architecture requirements, and deliver an actionable development sprint roadmap."
                : "Book a free discovery call with our engineering leadership. We will evaluate your requirements, design an actionable systems architecture, and provide a prototype roadmap."}
            </p>
            <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => openConsultationModal(service.title)}
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-[#FF5A1F] text-white font-bold text-sm hover:bg-[#ff6d38] transition-all shadow-[0_10px_25px_-5px_rgba(255,90,31,0.5)] group cursor-pointer"
              >
                <span>Book A Free Call</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
